import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { FileSaverService } from 'ngx-filesaver';
import { finalize, take, takeUntil } from 'rxjs';
import { DateTime } from 'luxon';

import { CardSetTransformationService, CoreService, DialogService, FormNavigationService, Option, SaveDataRequest, SpinnerService } from 'dspx-core';

import { API_ENDPOINT_KEYS, API_ENDPOINTS, ApiBaseRoute } from '../../constants/api-endpoints.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import {
  AppSelector,
  ButtonTitle,
  ComponentSelectors,
  defaultAppointmentHour,
  FORM_CUSTOM_ACTIONS,
  FORM_IDENTIFIERS,
  minWaitForButtonClickToCompleteMs,
  NAV_TAB_NUMBERS,
  NOTIFICATION_CODES,
  OPTION_SETS,
  OPTION_SET_NAMES,
  PAGE_NUMBERS,
  POP_UP_CODES,
  QUESTION_CODES,
  SAVE_RESULT_STATUS,
  UiDateFormat,
  PopupSelector,
  QUESTION_PATHS,
  RowSelectionLimit,
  DASHBOARD_EVENT
} from '../../constants/app-data.constants';

import { InspectionGridFilterRequest } from '../../interfaces/inspection-grid-filter-request.interface';

import { DateTimeHelper } from '../../helpers/date-time-helper';
import { DateTimeValidator } from '../../helpers/date-time-validator';
import { MapperHelper } from '../../helpers/mapper';
import { JsonHelper } from '../../helpers/json-helper';

import { HtmlHelperService } from '../../services/html-helper.service';
import { ServiceWorkerService } from '../../services/service-worker.service';
import { AppDataService } from '../../services/app-data.service';
import { ExcelImportService } from '../../services/excel-import.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { ErrorService } from '../../services/error.service';

import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-inspection-list',
  templateUrl: './inspection-list.component.html',
  styleUrls: ['./inspection-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InspectionListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  activeDialog: string = '';
  rowActionInspection: any;
  bulkActionInspections: any[] = [];
  inspectionDataGridDspxControl: any;
  inspectionDataGrid: any;
  inspectionGridDataType: string;
  batchId: string;
  actionButtonColumnSettings: any;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected excelImportService: ExcelImportService,
    protected coreService: CoreService,
    private errorService: ErrorService,
    private fileSaverService: FileSaverService,
    protected formNavigationService: FormNavigationService) {
    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);

    this.initialise(
      TEMPLATES.inspectionList,
      () => API_ENDPOINTS.dashboard.getInspections,
      true);
  }

  ngOnInit(): void {

    const logPrefix = 'ngOnInit';
    this.logger.log(`${logPrefix}: enter`);

    this.ignoreCanDeactivateGuard = true;

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap: ParamMap) => {
      const batchId = paramMap.get('id');
      if (batchId) {
        this.batchId = batchId;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getInspectionListConfigForBatch;
      }
    });

    this.coreService.applicationFunctions['validateScheduleDate'] = (args: any[]) =>
      DateTimeValidator.validateDateFormat(args) &&
      DateTimeValidator.validateScheduleDate(args) &&
      DateTimeValidator.validateScheduleDateTooFarAhead(args);
    this.coreService.applicationFunctions['canAddAppointment'] = () => this.canShowAlterGridDataTypeButton();
    this.coreService.applicationFunctions['canGenerateLetter'] = () => this.canShowAlterGridDataTypeButton();
    this.coreService.applicationFunctions['canAssignInspection'] = () => this.canShowAlterGridDataTypeButton();
    this.coreService.applicationFunctions['canUnassignInspection'] = () => this.canShowAlterGridDataTypeButton();
    this.coreService.applicationFunctions['canDownloadInspectionLetters'] = () => this.canShowAlterGridDataTypeButton();
    this.coreService.applicationFunctions['canSelectTopTwenty'] = () => this.canShowSelectTopTwentyButton();
    this.coreService.applicationFunctions['canSelectDate'] = () => this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.addAppointment;
    this.coreService.applicationFunctions['canReturnToDefaultView'] = 
    () => 
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.addAppointment ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.generateLetter ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.assignInspection ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.unassignInspection ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.downloadInspectionLetters;
    this.coreService.applicationFunctions['canDummy'] = () => false;

    this.coreService.applicationFunctions['canSubmitGenerateLetter'] = () => this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.generateLetter;
    this.coreService.applicationFunctions['canSelectInspector'] = () => this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.assignInspection;
    this.coreService.applicationFunctions['canSubmitUnassignInspections'] = () => this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.unassignInspection;
    this.coreService.applicationFunctions['canSubmitDownloadLetters'] = () => this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.downloadInspectionLetters;
       
    this.coreService.applicationFunctions['addAppointment'] = () => this.loadInspectionListDataAndState(FORM_CUSTOM_ACTIONS.inspectionListGridDataType.addAppointment);
    this.coreService.applicationFunctions['generateLetter'] = () => this.loadInspectionListDataAndState(FORM_CUSTOM_ACTIONS.inspectionListGridDataType.generateLetter);
    this.coreService.applicationFunctions['assignInspection'] = () => this.loadInspectionListDataAndState(FORM_CUSTOM_ACTIONS.inspectionListGridDataType.assignInspection);
    this.coreService.applicationFunctions['unassignInspection'] = () => this.loadInspectionListDataAndState(FORM_CUSTOM_ACTIONS.inspectionListGridDataType.unassignInspection);
    this.coreService.applicationFunctions['downloadInspectionLetters'] = () => this.loadInspectionListDataAndState(FORM_CUSTOM_ACTIONS.inspectionListGridDataType.downloadInspectionLetters);
    this.coreService.applicationFunctions['selectTopTwenty'] = () => this.selectTopTwenty();
    this.coreService.applicationFunctions['returnToDefaultView'] = () => this.loadInspectionListDataAndState(FORM_CUSTOM_ACTIONS.inspectionListGridDataType.returnToDefaultView);
    
    this.coreService.applicationFunctions['selectDate'] = () => this.openDialogForAddAppointmentBulk();
    this.coreService.applicationFunctions['submitGenerateLetters'] = () => this.submitGenerateLetters();
    this.coreService.applicationFunctions['selectInspector'] = () => this.openDialogForAssignInspectionBulk();
    this.coreService.applicationFunctions['submitUnassignInspections'] = () => this.submitUnassignInspections();
    this.coreService.applicationFunctions['submitDownloadLetters'] = () => this.submitDownloadLetters();
    this.coreService.applicationFunctions['exportToCsvFile'] = () => this.exportToCsvFile();
    this.coreService.applicationFunctions['fullExportToCsvFile'] = () => this.fullExportToCsvFile();

    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();
    this.waitForServiceWorker();

    this.logger.log(`${logPrefix}: leave`);
  }

  ngOnDestroy(): void {
    this.completeDestroy();
  }

  formLoad(event: any): void {    

    const logPrefix = 'formLoad';
    this.logger.log(`${logPrefix}: enter`);

    this.configData = event.configData;
    this.loadFilteredInspectionListData();
    this.setSelectedInspectionsAndDataGrid();
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.inspectionList.photo] = FORM_IDENTIFIERS.inspection.noAccessResponsePhoto;
    this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.inspectionList.viewPhoto] = FORM_IDENTIFIERS.inspection.noAccessResponsePhoto;

    this.logger.log(`${logPrefix}: leave`);
  }

  filterData(event: any): void { 
    if (event.action === DASHBOARD_EVENT.filter.clear) {
      this.clearSearchInput();
    }
    this.loadFilteredInspectionListData();
  }

  private loadFilteredInspectionListData(): void {
    const filterRequest = this.getFilterRequest();
    this.loadGridData(filterRequest, () => {
      // Hiding the status bar in the "form navigation" page, because we already have one in the "inspections" page.
      this.baseForm.setStatusBar(
        `tab${NAV_TAB_NUMBERS.inspectionList.formNavigation}`,
        `page${PAGE_NUMBERS.inspectionList.formNavigation}`,
        null);
      this.clearSelectedItemsOnInspectionsDataGrid();
    });
  }

  private getFilterRequest(): InspectionGridFilterRequest {
    const searchQuestionModel = this.getQuestionValue(this.model, ...QUESTION_PATHS.inspectionList.search);
    const filterQuestionModel = this.getQuestionValue(this.model, ...QUESTION_PATHS.inspectionList.filter);

    return {
      gridDataType: this.inspectionGridDataType,
      batchId: this.batchId,
      searchTerm: searchQuestionModel,
      alert: filterQuestionModel?.alert,
      status: filterQuestionModel?.status,
      surveyForm: filterQuestionModel?.surveyForm,
      scheduledOn: filterQuestionModel?.scheduledOn,
      inspectionPeriodEndScheduledOn: filterQuestionModel?.inspectionPeriodEndScheduledOn,
      assignedTo: filterQuestionModel?.assignedTo,
      suburb: filterQuestionModel?.suburb,
      productTypeCode: filterQuestionModel?.productTypeCode,
      areaOffice: filterQuestionModel?.areaOffice
    };
  }

  private canShowAlterGridDataTypeButton(): boolean {    
    if ((this.inspectionGridDataType === undefined ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.returnToDefaultView) &&
      !this.batchId
    ) {
      return true;
    }

    return false;
  }

  private canShowSelectTopTwentyButton(): boolean {    
    if ((this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.addAppointment ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.generateLetter ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.downloadInspectionLetters ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.assignInspection ||
      this.inspectionGridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.unassignInspection
    ) &&
      !this.batchId
    ) {
      return true;
    }

    return false;
  }

  private loadInspectionListDataAndState(gridDataType: string): void {
    this.clearSelectedItemsOnInspectionsDataGrid();
    if(gridDataType === FORM_CUSTOM_ACTIONS.inspectionListGridDataType.returnToDefaultView) {
      this.toggleSelectAllColumnOnInspectionsDataGrid(false);
      this.toggleActionsColumn(true);
    }
    else {
      this.toggleSelectAllColumnOnInspectionsDataGrid(true);
      this.toggleActionsColumn(false);
    }

    this.inspectionGridDataType = gridDataType;
    this.loadFilteredInspectionListData();
  }

  selectTopTwenty(): void {
    setTimeout(() => {
      const checkboxes = this.htmlHelperService.getTableInputList(AppSelector, ComponentSelectors.inspectionListForm.inspectionList,'checkbox');
      checkboxes.forEach((cb: any, index) => {
        if (index < RowSelectionLimit && !cb.checked) {
          cb.click();
        }
      });
    });
  }

  private clearSelectedItemsOnInspectionsDataGrid(): void {
    this.inspectionDataGrid.selectedRecords = [];
    this.inspectionDataGrid.tableInstance._selection = [];
    this.inspectionDataGrid.tableInstance.selectionKeys = {};
  }

  private toggleSelectAllColumnOnInspectionsDataGrid(shouldShow: boolean) {
    const checkboxSelectionProperty = 'props.dashboardOptions.checkboxSelection';
    this.inspectionDataGridDspxControl._observers[checkboxSelectionProperty].value = shouldShow;
  }

  private toggleActionsColumn(shouldShow: boolean) {
    const dashboardOptionsProperty = 'props.dashboardOptions';

    if(!this.actionButtonColumnSettings) {
      this.actionButtonColumnSettings = {
        allowView: this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.allowView,
        allowEdit: this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.allowEdit,
        actionButtons: this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.actionButtons
      };
    }

    if(!shouldShow) {
      this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.actionButtons = []
      this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.allowView = false
      this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.allowEdit = false
    }
    else {
      this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.actionButtons = this.actionButtonColumnSettings.actionButtons;
      this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.allowView = this.actionButtonColumnSettings.allowView;
      this.inspectionDataGridDspxControl._observers[dashboardOptionsProperty].value.allowEdit = this.actionButtonColumnSettings.allowEdit;
    }
  }

  actionButton_inspectionPlaceholder_datagrid_addAppointment_Clicked(event: any): void {
    this.openDialogForSaveAppointment(event.event.data, POP_UP_CODES.inspectionList.addAppointment, false);
  }

  actionButton_inspectionPlaceholder_datagrid_modifyAppointment_Clicked(event: any): void {
    this.openDialogForSaveAppointment(event.event.data, POP_UP_CODES.inspectionList.modifyAppointment, false);
  }

  actionButton_inspectionPlaceholder_datagrid_cancelAppointment_Clicked(event: any): void {
    this.openDialogForSaveAppointment(event.event.data, POP_UP_CODES.inspectionList.cancelAppointment, true);
  }

  isBoolStringTrue = (boolString) => boolString.toLowerCase() === true.toString();

  actionButton_inspectionPlaceholder_datagrid_reviewInspection_Clicked(event: any): void {
    if (this.isBoolStringTrue(event.event.data.canAccessProperty)) {
      this.redirectToInspectionDetailPage(event);
    }
    else {
      this.openDialogForNoPropertyAccess(event.event.data, POP_UP_CODES.inspectionList.viewNoAccessDetails);
    }
  }

  actionButton_inspectionPlaceholder_datagrid_qaReviewInspection_Clicked(event: any): void {
    if (this.isBoolStringTrue(event.event.data.canAccessProperty)) {

      if (Number(event.event.data.statusId) === OPTION_SETS.inspectionList.inspectionStatus.submittedToQA) {
        this.postInspectionAction(
          API_ENDPOINTS.inspectionList.reviewInspectionByQA,
          {
            InspectionId: event.event.data.inspectionId,
            batchId: event.event.data.batchId,
            expectedLastUpdateTime: event.event.data.lastUpdatedTime
          }
        );

      }
      this.redirectToInspectionDetailPage(event);
    }
    else {
      this.openDialogForNoPropertyAccess(event.event.data, POP_UP_CODES.inspectionList.viewNoAccessDetailsForQA);
    }
  }

  actionButton_inspectionPlaceholder_datagrid_rejectInspection_Clicked(event: any): void {
    if (this.isBoolStringTrue(event.event.data.canAccessProperty)) {
      this.redirectToInspectionDetailPage(event);
    }
    else if (this.isBoolStringTrue(event.event.data.canReviewInspection)) {
      this.openDialogForNoPropertyAccess(event.event.data, POP_UP_CODES.inspectionList.viewNoAccessDetails);
    }
    else if (this.isBoolStringTrue(event.event.data.canQAReviewInspection) || this.isBoolStringTrue(event.event.data.canRejectInspection)) {
      this.openDialogForNoPropertyAccess(event.event.data, POP_UP_CODES.inspectionList.viewNoAccessDetailsForQA);
    }
    else {
      console.error('No valid action found for Reject Inspection button');
    }
  }

  actionButton_inspectionPlaceholder_datagrid_downloadLetter_Clicked(event: any): void {
    this.downloadAppointmentLetter(event?.event?.data?.inspectionId);
  }

  actionButton_inspectionPlaceholder_datagrid_custom_getSelectedItems_Clicked(event: any): void {
    this.bulkActionInspections = event?.event?.data?.selectedItems;

    if(!this.inspectionDataGridDspxControl) {
      this.inspectionDataGridDspxControl = event?.field;
    }
    
    if(!this.inspectionDataGrid) {
      this.inspectionDataGrid = event?.field?.formControl?._fields[0]?.props?.componentInstance;
    }
  }

  actionButton_inspectionPlaceholder_datagrid_viewPropertyDetails_Clicked(event: any): void {
    this.openDialogForInspection(event?.event?.data, POP_UP_CODES.inspectionList.propertySummary);
  }

  actionButton_inspectionPlaceholder_datagrid_startInspection_Clicked(event: any): void {
    this.openDialogForInspection(event?.event?.data, POP_UP_CODES.inspectionList.startInspectionView);
  }

  actionButton_inspectionPlaceholder_datagrid_resumeInspection_Clicked(event: any): void {
    const inspection = event?.event?.data;
    this.rowActionInspection = inspection;

    if (this.shouldShowStartInspectionPopup(parseInt(inspection.statusId), inspection.isEmptyResponse, parseInt(inspection.reasonForNoAccess))) {
      this.openDialogForInspection(inspection, POP_UP_CODES.inspectionList.startInspectionView);
    }
    else if (Number(inspection.statusId) === OPTION_SETS.inspectionList.inspectionStatus.rework) {
      this.postInspectionAction(
        API_ENDPOINTS.inspectionList.save,
        {
          ...this.getSaveDataRequest({ resetResponse: false }),
          customSaveData: {
            statusId: OPTION_SETS.inspectionList.inspectionStatus.inspectionInProgress,
            canAccessProperty: true,
            inspectionStartDate: DateTimeHelper.now().toISO()
          }
        },
        null,
        false,
        false,
        true
      );
    }
    else {
      this.redirectToInspectionDetailPage(event);
    }
  }

  // suppress warning by declaring this method
  actionButton_inspectionPlaceholder_datagrid_doubleClick_Clicked(event:any): void {
  }

  actionButton_inspectionPlaceholder_datagrid_updateInspectionStatus_Clicked(event: any): void {
    this.openDialogForInspection(event?.event?.data, POP_UP_CODES.inspectionList.updateInspectionStatus);
  }

  dialogButton_addAppointment_Clicked(event: any): void {
    this.saveAppointment(event, POP_UP_CODES.inspectionList.addAppointmentResult);
  }

  dialogButton_addAppointmentBulk_Clicked(event: any): void {
    const inspectionIds = this.getBulkIdsOrShowNoSelection(i => i.inspectionId);

    this.postInspectionAction(
      API_ENDPOINTS.inspectionList.saveAppointmentDateBulk,
      {
        appointmentDate: DateTimeHelper.getIsoDateString(event.targetedInspectionDate, UiDateFormat, defaultAppointmentHour),
        targetedInspectionDateAmPm: event.targetedInspectionDateAmPm,
        inspectionPeriodEndDate: DateTimeHelper.getIsoDateString(event.inspectionPeriodEndDate, UiDateFormat, defaultAppointmentHour),
        inspectionIdCollection: inspectionIds
      },
      POP_UP_CODES.inspectionList.addAppointmentBulkResult);
  }

  dialogButton_modifyAppointment_Clicked(event: any): void {
    this.saveAppointment(event, POP_UP_CODES.inspectionList.modifyAppointmentResult);
  }

  dialogButton_cancelAppointment_Clicked(event: any): void {
    this.saveAppointment({...this.rowActionInspection, isCancelled: true },
      POP_UP_CODES.inspectionList.cancelAppointmentResult);
  }

  dialogButton_viewNoAccessDetails_Clicked(event: any): void {
    const shouldAcceptInspection = event.saveActionType === OPTION_SETS.inspectionList.saveActionType.approve;

    if(shouldAcceptInspection) {
      this.acceptOrRejectInspection({...this.rowActionInspection, ...event},
      API_ENDPOINTS.inspectionList.acceptInspection,
      POP_UP_CODES.inspectionList.acceptInspectionResult);
    }
    else {
      this.acceptOrRejectInspection({...this.rowActionInspection, ...event},
      API_ENDPOINTS.inspectionList.rejectInspection,
      POP_UP_CODES.inspectionList.rejectInspectionResult);
    }
  }

  dialogButton_viewNoAccessDetailsForQA_Clicked(event: any): void {
    this.acceptOrRejectInspection({...this.rowActionInspection, ...event},
      API_ENDPOINTS.inspectionList.rejectInspectionByQA,
      POP_UP_CODES.inspectionList.rejectInspectionByQAResult);
  }

  dialogButton_assignInspectionBulk_Clicked(event: any): void {
    const propertyIds = this.getBulkIdsOrShowNoSelection(i => i.pin);

    this.postInspectionAction(
      API_ENDPOINTS.inspectionList.assignInspectionBulk,
      {
        propertyIdCollection: propertyIds,
        assigneeUserId: event?.inspectorForBulkAssign
      },
      POP_UP_CODES.inspectionList.assignInspectionBulkResult);
  }

  dialogButton_startInspectionView_Clicked(event: any): void {
    const isClientContacted = MapperHelper.toBoolOrNull(event.contactClient);
    const isClientAvailable = MapperHelper.toBoolOrNull(event.confirmClientAvailability);

    switch (parseInt(event.propertyAccess)) {
      case OPTION_SETS.inspectionList.propertyAccess.yesIHaveAccess:
        this.rowActionInspection.template = parseInt(event.surveyForm);
        this.postInspectionAction(
          API_ENDPOINTS.inspectionList.save,
          {
            ...this.getSaveDataRequest(),
            customSaveData: {
              statusId: OPTION_SETS.inspectionList.inspectionStatus.inspectionInProgress,
              canAccessProperty: true,
              isClientContacted: isClientContacted,
              isClientAvailable: isClientAvailable,
              inspectionStartDate: DateTimeHelper.now().toISO(),
              inspectionCompletedDate: null
            }            
          },
          null,
          false,
          false,
          true
        );
        break;
      case OPTION_SETS.inspectionList.propertyAccess.noAccess:       

        if (this.wasLastAccessedToday(event.lastUpdatedNoAccessReason, event.numberOfAttempts)) {
          this.activeDialog = POP_UP_CODES.inspectionList.noAccessNotAllowed;
          this.dialogService.showPopup(this.activeDialog);
        }
        else if (this.canProceedWithNoAccessAttempt(event.numberOfAttempts, parseInt(event.reasonForNoAccess))
        ) {
          this.postInspectionAction(
            API_ENDPOINTS.inspectionList.syncCompletedInspections,
            {
              inspections: [
                {
                  inspectionId: this.rowActionInspection?.inspectionId,
                  template: this.rowActionInspection?.template,
                  templateId: this.rowActionInspection?.template,
                  pin: this.rowActionInspection?.pin,
                  inspectionCompletedDate: DateTimeHelper.now().toISO(),
                  canAccessProperty: false,
                  noAccessResponses: parseInt(event?.reasonForNoAccess) > 0 ? [{
                    comments: event?.comments,
                    reasonForNoAccess: parseInt(event?.reasonForNoAccess),
                    createdOn: DateTimeHelper.now().toISO(),
                    attachmentFile: event?.photo                    
                  }] : []
                }
              ],
              shouldUpdateStatus: true,
              isClientContacted: isClientContacted,
              isClientAvailable: isClientAvailable,
            },
            null,
            false,
            false,
            false,
            NOTIFICATION_CODES.inspectionList.saveSuccessNotification
          );
        }        
        else {
          this.postInspectionAction(
            API_ENDPOINTS.inspectionList.syncCompletedInspections,
            {
              inspections: [
                {
                  inspectionId: this.rowActionInspection?.inspectionId,
                  template: this.rowActionInspection?.template,
                  templateId: this.rowActionInspection?.template,
                  pin: this.rowActionInspection?.pin,                  
                  inspectionCompletedDate: DateTimeHelper.now().toISO(),
                  canAccessProperty: false,
                  noAccessResponses: [{
                    comments: event?.comments,
                    reasonForNoAccess: parseInt(event?.reasonForNoAccess),
                    createdOn: DateTimeHelper.now().toISO(),
                    attachmentFile: event?.photo                    
                  }]
                }
              ],
              shouldUpdateStatus: false,
              isClientContacted: isClientContacted,
              isClientAvailable: isClientAvailable,
            },
            null,
            false,
            false,
            false,
            NOTIFICATION_CODES.inspectionList.saveSuccessNotification
          );
        }

        break;
      default:
        console.error('No valid action found for Start Inspection submit button');
        break;
    }
  }

  dialogButton_updateInspectionStatus_Clicked(event: any): void {
    this.postInspectionAction(
      API_ENDPOINTS.inspectionList.save,
          {
            ...this.getSaveDataRequest(),
            customSaveData: {
              statusId: parseInt(event.applicableStatus)
            }
          },
      null,
      false,
      false,
      false,
      NOTIFICATION_CODES.inspectionList.saveSuccessNotification
    );
  }
 
  private shouldShowStartInspectionPopup(statusId: number, isEmptyResponse: boolean, reasonForNoAccess: number): boolean {
    return (statusId === OPTION_SETS.inspectionList.inspectionStatus.rework && isEmptyResponse) ||
           (statusId === OPTION_SETS.inspectionList.inspectionStatus.inspectionInProgress && isEmptyResponse && reasonForNoAccess > 0);
  }

  private canProceedWithNoAccessAttempt(    
    noOfAttempts: number,
    reasonForNoAccess: number): boolean {
    return noOfAttempts >= 1 || 
           reasonForNoAccess === OPTION_SETS.inspectionList.reasonForNoAccess.personalSafety ||
           reasonForNoAccess === OPTION_SETS.inspectionList.reasonForNoAccess.propertyDemolished ||
           reasonForNoAccess === OPTION_SETS.inspectionList.reasonForNoAccess.propertyNotLocated ||
           reasonForNoAccess === OPTION_SETS.inspectionList.reasonForNoAccess.noLikelihoodOfEntry
  };

  private wasLastAccessedToday(lastUpdatedNoAccessReason: string, noOfAttempts: number): boolean {
    if (lastUpdatedNoAccessReason != undefined) {      
      let date = new Date(lastUpdatedNoAccessReason);
      let today = DateTimeHelper.now().toJSDate();
      
      date.setHours(0);
      date.setMinutes(0);
      today.setHours(0);
      today.setMinutes(0);
      
      const diffDays = (today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);

      if (diffDays < 1 && noOfAttempts === 1) {
        return true;
      }
    }

    return false;
  }

  private openDialogForSaveAppointment(model: any, popupCode: string, isCancelled: boolean): void {
    this.activeDialog = popupCode;
    this.rowActionInspection = model;

    const formatDateTime = (dateTime: Date) => dateTime ? DateTime.fromJSDate(dateTime).toFormat(UiDateFormat) : '';
    this.dialogService.showPopup(this.activeDialog, 
      { 
        inspectionId: model?.inspectionId,
        inspectionDateType: model?.inspectionDateType,
        targetedInspectionDate: formatDateTime(model?.scheduledOn),
        targetedInspectionDateAmPm: model?.targetedInspectionDateAmPm?.toLowerCase(),
        inspectionPeriodEndDate: formatDateTime(model?.inspectionPeriodEndDate),
        pin: model?.pin,
        isCancelled: isCancelled,
        lastUpdatedTime: model?.lastUpdatedTime
      },
      {
        tenantContactName: model?.tenantContactName,
        tenantContactNumber: model?.tenantContactNumber,
        assignedTo: model?.assignedTo
      });
  }

  private openDialogForAddAppointmentBulk() {
    const onSetCompletedCallBack = () => {
      this.activeDialog = this.bulkActionInspections.length === 0 ? 
                         POP_UP_CODES.inspectionList.noInspectionSelected :
                         POP_UP_CODES.inspectionList.addAppointmentBulk;
      this.dialogService.showPopup(this.activeDialog, {}, {});
    };
    this.setSelectedInspectionsAndDataGridAsync(onSetCompletedCallBack);
  }

  private openDialogForAssignInspectionBulk(): void {
    const onSetCompletedCallBack = () => {
      this.activeDialog = this.bulkActionInspections.length === 0 ? 
                         POP_UP_CODES.inspectionList.noInspectionSelected :
                         POP_UP_CODES.inspectionList.assignInspectionBulk;
      this.dialogService.showPopup(this.activeDialog, {}, {});
    };
    this.setSelectedInspectionsAndDataGridAsync(onSetCompletedCallBack);
  }

  private setSelectedInspectionsAndDataGrid(): void {
    // this will trigger actionButton_inspectionPlaceholder_datagrid_custom_getSelectedItems_Clicked which will set the selected items and data grid
    this.htmlHelperService.clickButtonByTitle(AppSelector, ComponentSelectors.inspectionListForm.inspectionList, ButtonTitle.inspectionListForm.getSelectedItems);
  }

  private setSelectedInspectionsAndDataGridAsync(onSetCompletedCallBack?: () => void) {
    this.setSelectedInspectionsAndDataGrid();
    setTimeout(() => {      
      onSetCompletedCallBack?.();
    }, minWaitForButtonClickToCompleteMs);
  }

  private saveAppointment(inspection: any, popupCode: string): void {
    this.postInspectionAction(
      API_ENDPOINTS.inspectionList.saveAppointment,
      {
        inspectionId: inspection.inspectionId,
        appointmentDate: DateTimeHelper.getIsoDateString(inspection.targetedInspectionDate, UiDateFormat, defaultAppointmentHour),
        targetedInspectionDateAmPm: inspection.targetedInspectionDateAmPm,
        inspectionPeriodEndDate: DateTimeHelper.getIsoDateString(inspection.inspectionPeriodEndDate?.toString(), UiDateFormat, defaultAppointmentHour),
        pin: inspection.pin,
        isCancelled: inspection.isCancelled,
        lastUpdatedTime: inspection.lastUpdatedTime
      },
      popupCode
    );
  }

  private postInspectionAction(
    apiEndPoint: string,
    payload: any,
    resultPopupCode?: string,
    isFileDownload?: boolean,
    shouldRefreshInspectionListAfterDownload?: boolean,
    shouldRedirectToInspectionDetailPage?: boolean,
    resultNotificationCode?: string
  ): void {
    this.spinnerService.showSpinner();
    this.appDataService.post(apiEndPoint, payload)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (resultPopupCode) {
          this.openDialogForResult(data, resultPopupCode);
          if (data?.inspectionSaveStatus === SAVE_RESULT_STATUS.inspection.success ||
            data?.bulkInspectionSaveStatus === SAVE_RESULT_STATUS.bulkInspection.success
          ) {
            this.loadFilteredInspectionListData();
          }
        }
        else if (resultNotificationCode) {
          if(data.message) {
            this.openDialogForResult({}, data.message);
          }
          else {
            this.dialogService.showNotification(resultNotificationCode);
            this.loadFilteredInspectionListData();
          }
        }

        if (isFileDownload) {
          try {
            this.downloadFile(data);
          }
          catch (error) {
            console.error('Error downloading file:', error);
            this.activeDialog = POP_UP_CODES.inspectionList.fileDownloadError;
            this.dialogService.showPopup(this.activeDialog);
          }

          if (shouldRefreshInspectionListAfterDownload) {
            this.loadFilteredInspectionListData();
          }
        }

        if(shouldRedirectToInspectionDetailPage) {
          this.redirectToInspectionDetailPage({ event: { data: this.rowActionInspection } });
        }
      });
  }

  private submitGenerateLetters(): void {
    const onSetCompletedCallBack = () => {
      if (this.bulkActionInspections.length === 0) {
        this.activeDialog = POP_UP_CODES.inspectionList.noInspectionSelected;
        this.dialogService.showPopup(this.activeDialog, {}, {});
      }
      else {
        const inspectionIds = this.getBulkIdsOrShowNoSelection(i => i.inspectionId);

        this.postInspectionAction(
          API_ENDPOINTS.inspectionList.generateAppointmentLetterBulk,
          {
            inspectionIdCollection: inspectionIds
          },
          POP_UP_CODES.inspectionList.generateLetterBulkResult);
      }
    };

    this.setSelectedInspectionsAndDataGridAsync(onSetCompletedCallBack);
  }

  private submitUnassignInspections(): void {
    const onSetCompletedCallBack = () => {
      if (this.bulkActionInspections.length === 0) {
        this.activeDialog = POP_UP_CODES.inspectionList.noInspectionSelected;
        this.dialogService.showPopup(this.activeDialog, {}, {});
      }
      else {
        const inspectionIds = this.getBulkIdsOrShowNoSelection(i => i.inspectionId);

        this.postInspectionAction(
          API_ENDPOINTS.inspectionList.unassignInspectionBulk,
          {
            inspectionIdCollection: inspectionIds
          },
          POP_UP_CODES.inspectionList.unassignInspectionBulkResult);
      }
    };

    this.setSelectedInspectionsAndDataGridAsync(onSetCompletedCallBack);
  }

  private submitDownloadLetters(): void {
    const onSetCompletedCallBack = () => {
      if (this.bulkActionInspections.length === 0) {
        this.activeDialog = POP_UP_CODES.inspectionList.noInspectionSelected;
        this.dialogService.showPopup(this.activeDialog, {}, {});
      }
      else {
        const inspectionIds = this.getBulkIdsOrShowNoSelection(i => i.inspectionId);

        this.postInspectionAction(
          API_ENDPOINTS.inspectionList.downloadAppointmentLetterBulk,
          {
            inspectionIdCollection: inspectionIds
          },
          '',
          true,
          true
        );
      }
    };

    this.setSelectedInspectionsAndDataGridAsync(onSetCompletedCallBack);
  }

  private openDialogForResult(model: any, popupCode: string): void {
    this.activeDialog = popupCode;
    let messagePlaceholders: any;

    const messageWithValues = (model?.details?.[POP_UP_CODES.resultPopupMessageKey] || '').replace('{{pin}}', this.rowActionInspection?.pin);

    switch(popupCode) {
      case POP_UP_CODES.inspectionList.addAppointmentResult:
        messagePlaceholders = { addAppointmentResultMessage: messageWithValues };
        break;
      case POP_UP_CODES.inspectionList.modifyAppointmentResult:
        messagePlaceholders = { modifyAppointmentResultMessage: messageWithValues };
        break;
      case POP_UP_CODES.inspectionList.cancelAppointmentResult:
        messagePlaceholders = { cancelAppointmentResultMessage: messageWithValues };
        break;
      case POP_UP_CODES.inspectionList.rejectInspectionResult:
        messagePlaceholders = { rejectInspectionResultMessage: messageWithValues };
        break;
      case POP_UP_CODES.inspectionList.rejectInspectionByQAResult:
        messagePlaceholders = { rejectInspectionByQAResultMessage: messageWithValues };
        break;
      case POP_UP_CODES.inspectionList.acceptInspectionResult:
        messagePlaceholders = { acceptInspectionResultMessage: messageWithValues };
        break;
      case POP_UP_CODES.inspectionList.addAppointmentBulkResult:
        messagePlaceholders = { addAppointmentBulkResultMessage: messageWithValues };
        break;
      case POP_UP_CODES.inspectionList.generateLetterBulkResult:
      case POP_UP_CODES.inspectionList.unassignInspectionBulkResult:
        messagePlaceholders = {};
        break;
      case POP_UP_CODES.inspectionList.assignInspectionBulkResult:
        messagePlaceholders = { assignInspectionBulkResultMessage: messageWithValues};
        break;
      default:
        messagePlaceholders = {};
        console.error('Unknown popup code:', popupCode);
    }
    this.dialogService.showPopup(this.activeDialog, {}, messagePlaceholders);
  }

  private redirectToInspectionDetailPage(event: any): void {
    const { template: templateId, versionNumber: versionNo, id } = event.event.data;
    this.router.navigate([`${ROUTE_CONSTANTS.inspection.prefix}/${templateId}/${versionNo}/${id}`]);
  }

  private downloadAppointmentLetter(inspectionId: number): void {
    this.postInspectionAction(
      `${API_ENDPOINTS.inspectionList.downloadAppointmentLetter}?inspectionId=${inspectionId}`,
      {},
      '',
      true
    )
  }

  private downloadFile(fileResponse: any): void {
    if (!fileResponse?.data) {
      throw new Error('No file data received');
    }

    const base64 = fileResponse.data;
    const contentType = fileResponse.contentType || 'application/octet-stream';
    const fileName = fileResponse.fileName || 'download.file';

    // Decode Base64 → binary
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: contentType });

    this.fileSaverService.save(blob, fileName);
  }

  private openDialogForNoPropertyAccess(inspection: any, popupCode: string): void {
    this.activeDialog = popupCode;
    this.rowActionInspection = inspection;
    this.spinnerService.showSpinner();
    this.appDataService.getData(
      `${API_ENDPOINTS.inspectionList.getInspectionWithNoAccess}?inspectionId=${inspection?.inspectionId}`,
      {}
    )
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        this.dialogService.showPopup(this.activeDialog,
          {
            inspectionId: data?.inspectionId,
            viewPhoto: data?.attachment
          },
          {
            tenantContactName: data?.tenantContactName,
            tenantContactNumber: data?.tenantContactNumber,
            address: data?.address,
            suburb: data?.suburb,
            postcode: data?.postcode,
            propertyType: data?.propertyType,
            assignedTo: data?.assignedTo,
            surveyForm: data?.surveyForm,
            reasonForNoAccess: data?.reasonForNoAccess,
            comments: data?.comments,
            numberOfAttempts: data?.numberOfAttempts
          });
      });
  }

  private acceptOrRejectInspection(inspection: any, apiEndpoint: string, resultPopupCode: string): void {
      this.postInspectionAction(apiEndpoint, {
        inspectionId: inspection.inspectionId,
        expectedLastUpdateTime: inspection.lastUpdatedTime,
        managerComments: inspection.feedback,
        assignedUserId: inspection.inspectorForRejection
      }, resultPopupCode);
  }

  private openDialogForInspection(inspection: any, popupCode: string): void {
    this.activeDialog = popupCode;
    this.rowActionInspection = inspection;

     this.dialogService.showPopup(this.activeDialog,
      {
        inspectionId: inspection?.inspectionId,
        numberOfAttempts: parseInt(inspection?.numberOfAttempts),
        lastUpdatedNoAccessReason: inspection?.lastUpdatedNoAccessReason,
        propertyCategory: inspection?.propertyCategory?.toLowerCase(),
        surveyForm: inspection?.template?.toString(),
        currentStatus: parseInt(inspection?.statusId)
      },
      {
        tenantContactName: inspection?.tenantContactName,
        tenantContactNumber: inspection?.tenantContactNumber,
        agencyName: inspection?.agencyName?.length === 0 ?
          'Not Available' : inspection?.agencyName,
        address: inspection?.address,
        suburb: inspection?.suburb,
        postcode: inspection?.postcode,
        propertyType: inspection?.propertyType,
        assignedTo: inspection?.assignedTo,
        surveyForm: inspection?.surveyForm,
        reasonForNoAccess: inspection?.reasonForNoAccess,
        comments: inspection?.comments,
        numberOfAttempts: inspection?.numberOfAttempts,
        pin: inspection?.pin,
        status: inspection?.status,
        previousAccessAttempt: this.getPreviousAccessAttemptHtml(inspection, popupCode)
      });
  }

  private getPreviousAccessAttemptHtml(inspection: any, popupCode: string): string {
    if (!(popupCode === POP_UP_CODES.inspectionList.startInspectionView && inspection && parseInt(inspection.numberOfAttempts) > 0)) {
      return '';
    }

    const getFieldHtml = (name: string, value: any) => `<p><b>${name}:</b> ${value}</p>`;

    const reasonForNoAccessCode: string = inspection.reasonForNoAccess?.trim() ?? '';
    const reasonForNoAccessText = this.baseForm.optionSets
      ?.[OPTION_SET_NAMES.inspectionList.reasonForNoAccess.toLowerCase()]
      ?.find((option: Option) => option.code === reasonForNoAccessCode)
      ?.text
      ?? '';
    const reasonForNoAccessHtml = reasonForNoAccessText.length > 0 ? getFieldHtml('Reason for NO ACCESS', reasonForNoAccessText) : '';

    const noAccessComments: string = inspection.noAccessComments?.trim() ?? '';
    const noAccessCommentsHtml = noAccessComments.length > 0 ? getFieldHtml('Comments', noAccessComments) : '';

    const noAccessPhotoModel: string = inspection.noAccessPhoto?.trim() ?? '';
    const [noAccessPhotoFileName, noAccessPhotoFileGuid] = noAccessPhotoModel.split(':');
    // NOTE: You could simply set the anchor's href to the download URL, but that would not work when offline, because the fetch interceptor would not be triggered.
    //       To trigger the fetch interceptor you need an explicit call to the fetch API, hence the ugly workaround below.
    const noAccessPhotoHtmlAnchorClassName = 'no-access-photo-anchor';
    const noAccessPhotoAnchor = `<a href="#" class="${noAccessPhotoHtmlAnchorClassName}">${noAccessPhotoFileName}</a>`;
    const noAccessPhotoHtml = noAccessPhotoModel.length > 0 ? getFieldHtml('Photo', noAccessPhotoAnchor) : '';
    setTimeout(
      () => document.querySelector(`${AppSelector} ${ComponentSelectors.inspectionListForm.inspectionList} ${PopupSelector} .${noAccessPhotoHtmlAnchorClassName}`)
        ?.addEventListener('click', event => {
          event.preventDefault();
          downloadNoAccessPhoto(noAccessPhotoFileName, noAccessPhotoFileGuid);
        }));
    const downloadNoAccessPhoto = async (fileName: string, fileGuid: string) => {
      const url = `/${ApiBaseRoute}/${API_ENDPOINTS.document.download}/${FORM_IDENTIFIERS.inspection.noAccessResponsePhoto}/${fileGuid}`;
      const response = await fetch(url);
      const blob = await response.blob();
      this.htmlHelperService.downloadBlob(blob, fileName);
    }

    const numberOfAttempts = parseInt(inspection.numberOfAttempts);
    const numberOfAttemptsHtml = getFieldHtml('Number of Attempts', numberOfAttempts);

    return reasonForNoAccessHtml + noAccessCommentsHtml + noAccessPhotoHtml + numberOfAttemptsHtml;
  }

  private getSaveDataRequest(options?: { resetResponse?: boolean }): SaveDataRequest {
    // The save request is used to update the status and is done in the inspection list page.
    // When the user saves in the inspection detail page, this will get overridden.
    const resetResponse = options?.resetResponse ?? true; // the default behaviour is to reset the response
    const response = resetResponse
      ? this.responseParserService.ensureFormModel(null) // JSON object containing no questions
      : null; // null means: "leave it unchanged".
    return {
      template: this.rowActionInspection.template,
      versionNumber: this.rowActionInspection.versionNumber,
      id: this.rowActionInspection.id,
      response: JsonHelper.stringifyOrNull(response),
      expectedLastUpdateTime: this.rowActionInspection.lastUpdatedTime,
      isSubmit: false
    };
  }
  
  private exportToCsvFile = () => this.appDataService.getCsv(API_ENDPOINTS.inspectionList.exportInspections, this.getFilterRequest(), 'Inspections');

  private fullExportToCsvFile = () => this.appDataService.getCsv(API_ENDPOINTS.inspectionList.fullExportInspections, this.getFilterRequest(), 'Inspections(FullExport)');

  private clearSearchInput(): void {
    this.setQuestionValue(this.model, ...QUESTION_PATHS.inspectionList.search, '');
  }

  private getBulkIdsOrShowNoSelection<T>(
    selector: (item: any) => T | null | undefined
  ): T[] | null {
    const ids = (this.bulkActionInspections ?? [])
      .map(selector)
      .filter((value): value is T => value != null);

    if (ids.length === 0) {
      this.activeDialog = POP_UP_CODES.inspectionList.noInspectionSelected;
      this.dialogService.showPopup(this.activeDialog);
      return null;
    }

    return ids;
  }
}
